import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { logger } from '../utils/logger';

/**
 * Location Context
 * Provides centralized user location management
 * Fetches location once at app startup and shares it across components
 */

export interface UserLocation {
  lat: number;
  lon: number;
  timestamp: number;
}

interface LocationContextType {
  location: UserLocation | null;
  isLoading: boolean;
  error: string | null;
  refetchLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const DEFAULT_LOCATION = {
  lat: 51.2194, // Antwerp, Belgium
  lon: 4.4025,
};

const LOCATION_CACHE_KEY = 'user_location_cache';
const LOCATION_CACHE_DURATION = 60 * 60 * 1000; // 1 hour

interface LocationProviderProps {
  children: ReactNode;
}

/**
 * Gets cached location if available and recent
 */
function getCachedLocation(): UserLocation | null {
  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (cached) {
      const location: UserLocation = JSON.parse(cached);
      // Check if cache is still valid (within 1 hour)
      if (Date.now() - location.timestamp < LOCATION_CACHE_DURATION) {
        logger.debug('Using cached location', { location });
        return location;
      }
    }
  } catch (error) {
    logger.warn('Error reading cached location', { error });
  }
  return null;
}

/**
 * Caches location in localStorage
 */
function cacheLocation(location: UserLocation): void {
  try {
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(location));
  } catch (error) {
    logger.warn('Error caching location', { error });
  }
}

/**
 * Fetches user location using browser geolocation API
 */
async function fetchUserLocation(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const location: UserLocation = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          timestamp: Date.now(),
        };
        logger.debug('Location fetched', { location });
        resolve(location);
      },
      error => {
        logger.warn('Geolocation error', { error });
        reject(error);
      },
      {
        timeout: 10000, // 10 seconds
        enableHighAccuracy: false, // Faster response with lower accuracy
        maximumAge: LOCATION_CACHE_DURATION, // Allow cached position up to 1 hour
      }
    );
  });
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLocation = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // First, try to use cached location
      const cached = getCachedLocation();
      if (cached) {
        setLocation(cached);
        setIsLoading(false);
        return;
      }

      // If no cache, fetch fresh location
      logger.info('Fetching user location...');
      const freshLocation = await fetchUserLocation();
      setLocation(freshLocation);
      cacheLocation(freshLocation);
      setIsLoading(false);
    } catch (err) {
      logger.warn('Failed to get user location, using default', { err });
      // Use default location (Antwerp)
      const defaultLocation: UserLocation = {
        ...DEFAULT_LOCATION,
        timestamp: Date.now(),
      };
      setLocation(defaultLocation);
      setError('Could not determine your location. Using default location (Antwerp, Belgium).');
      setIsLoading(false);
    }
  };

  const refetchLocation = async (): Promise<void> => {
    logger.info('Refetching location...');
    // Clear cache to force fresh fetch
    localStorage.removeItem(LOCATION_CACHE_KEY);
    await loadLocation();
  };

  // Fetch location on mount
  useEffect(() => {
    loadLocation();
  }, []);

  return (
    <LocationContext.Provider
      value={{
        location,
        isLoading,
        error,
        refetchLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

/**
 * Custom hook to use location context
 */
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
